import { DataTable, DataTableColumn, DataTableOptions, DataType, FilterType, useNotify } from '@globaleris/ui.library'
import { Box, Button, TablePagination, Typography } from '@material-ui/core'
import { AddPhotoAlternate, FileCopy, Photo } from '@material-ui/icons'
import { DropzoneArea } from 'material-ui-dropzone'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getImages, uploadImage } from '../../../api/Argentia/endpoints/atlasPosts/atlasPosts.api'
import { AtlasPostImage, AtlasPostImagePage } from '../../../api/Argentia/endpoints/atlasPosts/model/atlasPosts'

interface Props {
  onUseCover: (imageRef: string) => void
  onInsert: (image: AtlasPostImage) => void
  onImagesLoaded: (images: AtlasPostImage[]) => void
}

const maxImagesPerUpload = 5
const maxImageSize = 10 * 1024 * 1024
const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const signatures: Record<string, number[]> = {
  'image/jpeg': [0xff, 0xd8, 0xff],
  'image/png': [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
}

const matchesImageSignature = (file: File): Promise<boolean> => new Promise(resolve => {
  const reader = new FileReader()
  reader.onerror = () => resolve(false)
  reader.onload = () => {
    const bytes = new Uint8Array(reader.result as ArrayBuffer)
    const signature = signatures[file.type]
    if (signature) return resolve(signature.every((byte, index) => bytes[index] === byte))
    if (file.type === 'image/gif') {
      const header = String.fromCharCode(...Array.from(bytes.slice(0, 6)))
      return resolve(header === 'GIF87a' || header === 'GIF89a')
    }
    if (file.type === 'image/webp') {
      const text = (start: number, end: number) => String.fromCharCode(...Array.from(bytes.slice(start, end)))
      return resolve(text(0, 4) === 'RIFF' && text(8, 12) === 'WEBP')
    }
    resolve(false)
  }
  reader.readAsArrayBuffer(file.slice(0, 12))
})

export const AtlasBlogImages: React.FC<Props> = ({ onUseCover, onInsert, onImagesLoaded }) => {
  const [t] = useTranslation(['atlasBlog', 'globalerisLibrary'])
  const { error, info } = useNotify()
  const [page, setPage] = useState(0)
  const [data, setData] = useState<AtlasPostImagePage>({ items: [], totalCount: 0, page: 1, pageSize: 20 })
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [reload, setReload] = useState(0)
  const [dropzoneKey, setDropzoneKey] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    getImages(page + 1, 20).then(result => {
      if (!active) return
      setData(result)
      onImagesLoaded(result.items)
    }).catch(() => { if (active) error(t('errors.images')) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [page, reload])

  const handleUpload = async () => {
    if (!files.length || uploading) return
    setUploading(true)
    let uploaded = 0
    try {
      for (const file of files.slice(0, maxImagesPerUpload)) {
        const expectedType = /\.jpe?g$/i.test(file.name) ? 'image/jpeg'
          : /\.png$/i.test(file.name) ? 'image/png'
            : /\.gif$/i.test(file.name) ? 'image/gif'
              : /\.webp$/i.test(file.name) ? 'image/webp' : null
        if (!expectedType || file.type !== expectedType || !file.size || file.size > maxImageSize ||
          !(await matchesImageSignature(file))) {
          error(t('errors.imageType'))
          continue
        }
        const image = await uploadImage(file)
        onImagesLoaded([image])
        uploaded += 1
      }
    } catch (_) {
      error(t('errors.upload'))
    } finally {
      if (uploaded) {
        info(t('messages.imagesUploaded', { count: uploaded }))
        setFiles([])
        setDropzoneKey(value => value + 1)
        setPage(0)
        setReload(value => value + 1)
      }
      setUploading(false)
    }
  }

  const copy = async (imageRef: string) => {
    try {
      await navigator.clipboard.writeText(imageRef)
      info(t('messages.copied'))
    } catch (_) {
      error(t('errors.copy'))
    }
  }

  const tableColumns: DataTableColumn[] = [
    { name: 'preview', label: t('images.preview'), type: DataType.HTML, columnWidth: 140, sortable: false },
    { name: 'fileName', label: t('images.fileName'), type: DataType.TEXT, sortable: false }
  ]
  const tableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    hideToolbar: true,
    search: false,
    filterType: FilterType.NONE,
    download: false,
    print: false,
    pagination: false,
    selectableRows: false,
    noData: { description: t('images.empty') },
    rowCustomActions: [
      { icon: <FileCopy fontSize="small" />, tooltip: t('images.copy'), onClick: row => copy(row.imageRef) },
      { icon: <Photo fontSize="small" />, tooltip: t('images.useCover'), onClick: row => onUseCover(row.imageRef) },
      { icon: <AddPhotoAlternate fontSize="small" />, tooltip: t('images.insert'), onClick: row => onInsert(row) }
    ]
  }
  const tableData = data.items.map(image => ({
    ...image,
    preview: image.previewUrl ? (
      <img
        src={image.previewUrl}
        alt={image.fileName}
        style={{ maxWidth: 120, maxHeight: 90, objectFit: 'contain' }}
      />
    ) : null
  }))

  return (
    <Box mt={3}>
      <Box mb={1}>
        <Typography variant="h6">{t('images.title')}</Typography>
      </Box>
      <DropzoneArea key={dropzoneKey} onChange={selected => setFiles(selected.slice(0, maxImagesPerUpload))}
        filesLimit={maxImagesPerUpload} acceptedFiles={acceptedImageTypes}
        maxFileSize={maxImageSize} showAlerts={false}
        dropzoneProps={{
          getFilesFromEvent: async event => {
            if ('dataTransfer' in event && event.type !== 'drop') {
              return event.dataTransfer.items
                ? Array.from(event.dataTransfer.items).filter(item => item.kind === 'file')
                : []
            }
            const selected = 'dataTransfer' in event
              ? Array.from(event.dataTransfer.files)
              : Array.from((event.target as HTMLInputElement).files || [])
            const available = Math.max(0, maxImagesPerUpload - files.length)
            const skipped = Math.max(0, selected.length - available)
            if (skipped) info(t('messages.imagesSkipped', { count: skipped }))
            return selected.slice(0, available)
          }
        }}
        getFileLimitExceedMessage={() => t('errors.imageLimit')}
        getDropRejectMessage={() => t('errors.imageType')}
        onAlert={(message, variant) => { if (variant === 'error') error(message) }}
        dropzoneText={t('images.dropzone')} />
      <Box display="flex" justifyContent="flex-end" my={2}>
        <Button variant="contained" color="primary" disabled={!files.length || uploading} onClick={handleUpload}>
          {t('images.upload')}
        </Button>
      </Box>
      <DataTable data={loading ? undefined : tableData} columns={tableColumns} options={tableOptions} />
      {!loading && (
        <TablePagination component="div" count={data.totalCount} page={page} rowsPerPage={20}
          rowsPerPageOptions={[20]} onChangePage={(_, value) => setPage(value)} />
      )}
    </Box>
  )
}
